// dependencies
import * as Sentry from "@sentry/react";
import React, { FormEvent, ReactElement, useEffect, useRef, useState } from "react";
import { z } from "zod";

// types
import { GetEmployeeDetailResponse } from "@performyard/service-contracts";

// components
import Alert from "../../../../../components/Alert";
import Button from "../../../../../components/Button";
import Container from "../../../../../components/Container";
import DatePicker from "../../../../../components/Inputs/DatePicker";
import FormModal from "../../../../../components/Modal/Templates/FormModal";
import Text from "../../../../../components/Text";
import { TextEditor } from "../../../../../components/TextEditor";
// error boundary fallback
import FieldErrorFallback from "../../../EmployeeDetails/components/EditEmployeeCard/FieldErrorFallback";

// hooks
import useForm from "../../../../../hooks/useForm";

// utils
import { getFieldInput, getValueForInputType } from "../../../utils/index";

// config
import Config from "../../../Config";
// styles
import css from "../../ViewFieldHistory.module.scss";

interface BaseProps {
	fieldData: GetEmployeeDetailResponse;
	isEditFieldModalOpen: boolean;
	setIsEditFieldModalOpen: (isOpen: boolean) => void;
	submitEditFieldForm: (formValues: EditFieldForm) => void;
	updatingField: boolean;
	isEmployeeManagedByIntegration: boolean;
}

export interface EditFieldForm {
	fieldValue?: any;
	effectiveDate?: Date | null;
	comment?: string;
}

const EditFieldModal = (props: BaseProps) => {
	const {
		fieldData,
		isEditFieldModalOpen,
		setIsEditFieldModalOpen,
		submitEditFieldForm,
		updatingField,
		isEmployeeManagedByIntegration,
	} = props;

	const formSchema = z.object({
		fieldValue: Config.inputSchema[fieldData?.type],
		effectiveDate: z.date().nullable(),
		comment: z.string().or(z.literal("")),
	});

	const [showValidationError, setShowValidationError] = useState(false);

	const editor = useRef<ReactElement | null>();

	const { state, setInitialState, updateForm, validate, resetForm } = useForm({
		schema: formSchema,
	});

	const initializeModal = () => {
		try {
			const fieldValue = getValueForInputType(fieldData?.type, fieldData?.value);

			setInitialState({
				fieldValue: fieldValue,
				effectiveDate: null,
				comment: "",
			});
			updateForm({
				fieldValue: fieldValue,
				effectiveDate: null,
				comment: "",
			});

			editor.current = (
				<TextEditor
					label="Comment"
					labelProps={{ size: "paragraph-3", weight: "semi-bold" }}
					initialValue=""
					onChange={(value) => updateForm({ comment: value })}
					placeholder="Your comment text here"
					minHeight={96}
					className={css.textEditor}
				/>
			);
		} catch (error) {
			Sentry.captureException(error, {
				contexts: {
					editFieldModal: {
						fieldId: fieldData?._id,
						fieldName: fieldData?.name,
						fieldType: fieldData?.type,
						employeeId: fieldData?.user?._id,
					},
				},
			});
			// Set safe defaults if initialization fails
			setInitialState({
				fieldValue: null,
				effectiveDate: null,
				comment: "",
			});
			updateForm({
				fieldValue: null,
				effectiveDate: null,
				comment: "",
			});
		}
	};

	useEffect(() => {
		if (isEditFieldModalOpen) {
			initializeModal();
		} else {
			resetForm();
			editor.current = null;
		}
		return () => {
			editor.current = null;
			setShowValidationError(false);
		};
	}, [isEditFieldModalOpen]);

	const handleSubmitEditFieldForm = (event: FormEvent<HTMLFormElement>) => {
		event.preventDefault();
		const validationResult = validate();
		if (validationResult.isValid) {
			submitEditFieldForm(state);
		}
	};

	return (
		<FormModal
			wrapperClassName={css.formModal}
			isOpen={isEditFieldModalOpen}
			closeOnClickOutside={false}
			handleClose={() => setIsEditFieldModalOpen(false)}
			onSubmit={(event) => handleSubmitEditFieldForm(event)}
			title={
				<Text size="sub-heading" weight="semi-bold">
					Edit Field
				</Text>
			}
			buttons={[
				<Button
					key="cancel-edit-field"
					testid="cancel-edit-field"
					onClick={() => setIsEditFieldModalOpen(false)}
				>
					Cancel
				</Button>,
				<Button key="edit-field" testid="edit-field" color="green" submit isRequesting={updatingField}>
					Submit
				</Button>,
			]}
		>
			<Container column gap="16px">
				<Sentry.ErrorBoundary
					fallback={
						<FieldErrorFallback
							field={fieldData}
							onUpdate={(value) => updateForm({ fieldValue: value?.fieldValue })}
						/>
					}
					onError={() => {
						Sentry.setContext("editFieldModal", {
							fieldId: fieldData?._id,
							fieldName: fieldData?.name,
							fieldType: fieldData?.type,
							employeeId: fieldData?.user?._id,
						});
					}}
				>
					{getFieldInput(
						fieldData,
						state.fieldValue,
						updateForm,
						showValidationError,
						isEmployeeManagedByIntegration
					)}
				</Sentry.ErrorBoundary>
				<Container column gap="8px">
					<DatePicker
						label="Effective Date"
						value={""}
						onChange={(value) => updateForm({ effectiveDate: value })}
						hasIcon
					/>
					<Alert
						message="Backdating effective dates may require you to delete no value placeholder entries."
						type="neutral"
					/>
				</Container>
				<div>{editor.current}</div>
			</Container>
		</FormModal>
	);
};

export default EditFieldModal;
